import {
  ActivityFilterScreen,
  ActivityFilterScreenHandle,
  ColumnData,
  ListCountProvider,
  SlideAction,
  SlideLayout,
  useSlideLayout,
  useSlideLayoutSafe,
  useSlidePanel,
  ViewMode,
} from '@gripp/shared-ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { ActivityFilters } from './activityFilters';
import { ActivityFoldOneColumn } from './activityFoldOneColumn';

const ACTIVITY_FILTER_PANEL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/components/activityFilterPage.tsx';
const ACTIVITY_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/routes.tsx';
const ACTIVITY_FILTER_SCREEN_SOURCE =
  'gripp-workspace/libs/shared-ui/src/components/filter/activityFilterScreen.tsx';
const ACTIVITY_FILTER_ROUTE_PATTERN = '/activities/filters';

export const ActivityFilterPage = () => {
  const navigate = useNavigate();
  const slideLayout = useSlideLayoutSafe();
  const onSlideChange = slideLayout?.onSlideChange;
  const filterRef = useRef<ActivityFilterScreenHandle>(null);

  const column = useMemo<ColumnData>(
    () => ({
      title: 'assets.menu.title',
      mainComponent: (
        <ActivityFilterScreen
          ref={filterRef}
          mode="page"
          isVisible={true}
          bottomBar={() => null}
        />
      ),
      headerComponent: (
        <HeaderResetButton onReset={() => filterRef.current?.onReset()} />
      ),
      showBackButton: true,
      onBackButtonPress: async () => {
        await onSlideChange?.(SlideAction.SINGLE);
        navigate('/activities', { state: { skipAutoSelect: true } });
      },
      disableScroll: true,
      debug: {
        id: 'activity-filters:right-filter',
        titleKey: 'assets.menu.title',
        component: 'ActivityFilterScreen',
        componentSource: ACTIVITY_FILTER_SCREEN_SOURCE,
        columnSource: ACTIVITY_FILTER_PANEL_SOURCE,
        ownerComponent: 'ActivityFilterPage',
        ownerSource: ACTIVITY_FILTER_PANEL_SOURCE,
        routePanelKey: 'activity-filters',
        routePattern: ACTIVITY_FILTER_ROUTE_PATTERN,
        routeSource: ACTIVITY_ROUTES_SOURCE,
        source: {
          owner: {
            file: ACTIVITY_FILTER_PANEL_SOURCE,
            symbol: 'ActivityFilterPage',
          },
          column: {
            file: ACTIVITY_FILTER_PANEL_SOURCE,
            symbol: 'column',
          },
          component: {
            file: ACTIVITY_FILTER_SCREEN_SOURCE,
            symbol: 'ActivityFilterScreen',
          },
          route: {
            file: ACTIVITY_ROUTES_SOURCE,
            symbol: 'activityRoutes',
          },
        },
      },
    }),
    [navigate, onSlideChange]
  );

  useSlidePanel('right', column, [column]);

  if (slideLayout) {
    return null;
  }

  return <ActivityFilterPageStandalone />;
};

const ActivityFilterPageStandalone = () => {
  const filterRef = useRef<ActivityFilterScreenHandle>(null);
  const onBackButtonPressRef = useRef<(() => void) | undefined>(undefined);

  const navigate = useNavigate();
  const { issueId } = useParams();
  const [count, setCount] = useState<number>();

  const handleDataLoaded = useCallback((_data: unknown[]) => undefined, []);

  const onIssueSelected = useCallback(
    (issueId: string) => {
      navigate(`/activities/${issueId}`);
    },
    [navigate]
  );

  const leftColumn = {
    title: 'activity.menu.title',
    count,
    mainComponent: (
      <ActivityLeftColumn
        onIssueSelected={onIssueSelected}
        showAssetName={true}
        selectedIssue={issueId}
        onDataLoaded={handleDataLoaded}
        hideFilterBar={true}
        onActivityCountChange={setCount}
      />
    ),
    headerComponent: <ActivityFilters />,
    disableScroll: true,
  };

  const rightColumn = {
    title: 'assets.menu.title',
    mainComponent: (
      <>
        <ActivityFilterScreen
          ref={filterRef}
          mode="page"
          isVisible={true}
          bottomBar={() => null}
        />
        <BackHandlerRegistrar onBackButtonPressRef={onBackButtonPressRef} />
      </>
    ),
    headerComponent: (
      <HeaderResetButton onReset={() => filterRef.current?.onReset()} />
    ),
    showBackButton: true,
    onBackButtonPress: () => onBackButtonPressRef.current?.(),
    disableScroll: true,
  };

  return (
    <ListCountProvider count={count}>
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        viewMode={ViewMode.DUAL}
      />
    </ListCountProvider>
  );
};

const ActivityLeftColumn = ({
  onIssueSelected,
  showAssetName,
  selectedIssue,
  onDataLoaded,
  hideFilterBar,
  onActivityCountChange,
}: {
  onIssueSelected: (issueId: string) => void;
  showAssetName: boolean;
  selectedIssue?: string;
  onDataLoaded: (data: unknown[]) => void;
  hideFilterBar: boolean;
  onActivityCountChange?: (count: number) => void;
}) => {
  const { onSlideChange, currentViewMode } = useSlideLayout();

  const handleIssueSelected = useCallback(
    (issueId: string) => {
      if (currentViewMode !== ViewMode.DUAL) {
        onSlideChange(SlideAction.DUAL);
      }
      onIssueSelected(issueId);
    },
    [onSlideChange, currentViewMode, onIssueSelected]
  );

  return (
    <ActivityFoldOneColumn
      onIssueSelected={handleIssueSelected}
      showAssetName={showAssetName}
      selectedIssue={selectedIssue}
      onDataLoaded={onDataLoaded}
      hideFilterBar={hideFilterBar}
      onActivityCountChange={onActivityCountChange}
    />
  );
};

const BackHandlerRegistrar = ({
  onBackButtonPressRef,
}: {
  onBackButtonPressRef: React.MutableRefObject<(() => void) | undefined>;
}) => {
  const navigate = useNavigate();
  const { onSlideChange } = useSlideLayout();

  onBackButtonPressRef.current = async () => {
    await onSlideChange(SlideAction.SINGLE);
    navigate('/activities', { state: { skipAutoSelect: true } });
  };

  return null;
};
